import { useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { selectCockpitPbmUsers } from '../../../model/selectors'
import type { IAttentionStatusesReminder } from '../types'

import { useAttentionReminder } from './useAttentionReminder'
import { useAttentionReminderScope } from './useAttentionReminderScope'

/**
 * `Remind PBM` for one partner's offering about one status: the partner's PBMs on that offering,
 * as the completeness strip, the planned next steps and the SOB list remind them. The service says
 * who was reminded today, so a row reads `Reminded today` across sessions.
 */
export const usePartnerPbmReminders = (
  partnerId: string,
  offering: string | null,
  bitValue: number
) => {
  const pbmUsers = useSelector(selectCockpitPbmUsers)
  const scope = useAttentionReminderScope(offering)

  const recipients = useMemo<IAttentionStatusesReminder[]>(() => {
    const seen = new Set<string>()

    return pbmUsers
      .filter(({ PartnerId, Offering, Email }) => {
        if (PartnerId !== partnerId || (offering && Offering !== offering)) {
          return false
        }

        if (!Email || seen.has(Email)) {
          return false
        }

        seen.add(Email)
        return true
      })
      .map(({ Email, FullName }) => ({ email: Email, name: FullName?.trim() || Email, partnerId }))
  }, [offering, partnerId, pbmUsers])

  const route = useMemo(() => ({ bitValue, actionId: 'remindPbm' as const }), [bitValue])
  const reminder = useAttentionReminder({ recipients, route, scope })
  const { remind, stateOf } = reminder
  const remindPbm = useCallback(() => void remind(), [remind])

  return {
    recipients,
    reminder,
    remindPbm,
    isReminded:
      recipients.length > 0 && recipients.every(({ email }) => stateOf(email) === 'remindedToday'),
    /* Busy from the first render until the service says who can be reminded, so the button
       never offers a reminder it then takes back. */
    isPending: reminder.isChecking || recipients.some(({ email }) => stateOf(email) === 'pending'),
  }
}
